import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Move these components OUTSIDE to prevent recreation on every render
// This fixes the "1 character at a time" bug
const InputField = ({ label, name, type = 'text', required = false, value, onChange, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2b3380]"
      {...props}
    />
  </div>
);

const SelectField = ({ label, name, options, required = false, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2b3380]"
    >
      <option value="">Select...</option>
      {options.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const SelectFieldWithData = ({ label, name, data, valueKey = 'id', labelKey = 'name', required = false, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2b3380]"
    >
      <option value="">Select...</option>
      {data.map(item => {
        const itemValue = typeof item === 'string' ? item : item[valueKey] || item[labelKey];
        const itemLabel = typeof item === 'string' ? item : 
                         typeof labelKey === 'function' ? labelKey(item) : item[labelKey];
        return (
          <option key={itemValue} value={itemValue}>
            {itemLabel}
          </option>
        );
      })}
    </select>
  </div>
);

const CheckboxField = ({ label, name, checked, onChange }) => (
  <div className="flex items-center">
    <input
      type="checkbox"
      name={name}
      checked={checked || false}
      onChange={onChange}
      className="w-4 h-4 text-[#2b3380] rounded mr-2"
    />
    <label className="text-sm font-medium">{label}</label>
  </div>
);

function EmployeeFormComprehensive({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    first_name: '', middle_name: '', last_name: '',
    date_of_birth: '', age: '', gender: '', blood_group: '', marital_status: '',
    profile_photo_url: '', personal_email: '', official_email: '', mobile_number: '',
    alternate_mobile: '', current_address: '', permanent_address: '',
    date_of_joining: '', probation_end_date: '', employment_type: 'Full-time',
    employee_status: 'Active', departments: [], designation: '',
    reporting_manager_id: '', secondary_manager_id: '', work_location: '',
    office_branch: '', employee_category: '', shift_timing: '10:00 AM - 7:00 PM',
    work_mode: 'Office', confirmation_date: '', last_working_date: '',
    notice_period_days: 30, separation_reason: '', official_id_card_number: '',
    basic_salary: 0, gross_salary: 0, net_salary: 0, ctc_annual: 0, ctc_monthly: 0,
    hra: 0, da: 0, conveyance_allowance: 0, medical_allowance: 0,
    special_allowance: 0, other_allowances: 0, pf_employee: 0, pf_employer: 0,
    esi_employee: 0, esi_employer: 0, professional_tax: 0, income_tax_tds: 0,
    minimum_wage_applicable: false, minimum_wage_amount: 0, salary_adjustments: 0,
    arrears: 0, bonus: 0, incentives: 0, commission: 0,
    bank_name: '', account_number: '', ifsc_code: '', bank_branch: '', payment_mode: 'Bank Transfer',
    pan_number: '', aadhaar_number: '', pf_account_number: '', esi_number: '',
    passport_number: '', passport_expiry: '', driving_license_number: '', dl_expiry: '',
    voter_id: '', ration_card_number: '', gst_number: '',
    naps_registration_number: '', naps_status: '', naps_stipend_amount: 0,
    naps_start_date: '', naps_end_date: '', naps_certificate_number: '', naps_trade_occupation: '',
    nats_registration_number: '', nats_training_period: '', nats_trade_name: '',
    nats_stipend: 0, nats_certificate_status: '',
    emergency_contact_name_1: '', emergency_contact_relationship_1: '', emergency_contact_phone_1: '',
    emergency_contact_name_2: '', emergency_contact_relationship_2: '', emergency_contact_phone_2: '',
    emergency_address: '', emergency_email: '',
    highest_qualification: '', university_board: '', year_of_passing: '',
    specialization_stream: '', additional_certifications: '', professional_memberships: '',
    skills_expertise: '', languages_known: '',
    previous_company_name: '', previous_designation: '', previous_salary: 0,
    previous_employment_duration: '', reason_for_leaving: '',
    experience_letter_available: false, relieving_letter_available: false,
    total_years_of_experience: 0,
    resume_url: '', offer_letter_url: '', appointment_letter_url: '',
    contract_document_url: '', address_proof_url: '', photo_id_url: '',
    total_leave_balance: 9, sick_leave_balance: 0, casual_leave_balance: 0,
    leaves_taken_current_year: 0, total_present_days: 0, total_absent_days: 0,
    late_coming_count: 0, half_days_count: 0, last_attendance_date: '',
    last_appraisal_date: '', next_appraisal_due_date: '',
    current_performance_rating: '', last_increment_percentage: 0,
    last_increment_date: '', promotion_history: '', notes: '', internal_comments: ''
  });

  // Master Data State for Dropdowns
  const [masterData, setMasterData] = useState({
    departments: [],
    locations: [],
    designations: [],
    shift_timings: [],
    employment_types: [],
    employee_levels: [],
    employees: []
  });

  useEffect(() => {
    fetchMasterData();
    if (isEdit) fetchEmployee();
  }, [id]);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [masterDataRes, employeesRes] = await Promise.all([
        axios.get(`${API}/master-data/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setMasterData({
        ...masterDataRes.data,
        employees: employeesRes.data || []
      });
    } catch (error) {
      console.error('Error fetching master data:', error);
      // Don't block form if master data fails
    }
  };

  const fetchEmployee = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({...formData, ...response.data});
    } catch (error) {
      setError('Failed to fetch employee');
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleDepartmentChange = useCallback((e) => {
    const departments = e.target.value.split(',').map(d => d.trim()).filter(d => d);
    setFormData(prev => ({ ...prev, departments }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (isEdit) {
        await axios.put(`${API}/employees/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/employees`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate('/employees');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: '👤' },
    { id: 'employment', label: 'Employment', icon: '💼' },
    { id: 'compensation', label: 'Compensation', icon: '💰' },
    { id: 'govt_ids', label: 'Govt IDs', icon: '🆔' },
    { id: 'naps_nats', label: 'NAPS/NATS', icon: '📋' },
    { id: 'emergency', label: 'Emergency', icon: '🚨' },
    { id: 'education', label: 'Education', icon: '🎓' },
    { id: 'previous', label: 'Prev Employment', icon: '📝' },
    { id: 'documents', label: 'Documents', icon: '📄' },
    { id: 'attendance', label: 'Attendance', icon: '📅' },
    { id: 'performance', label: 'Performance', icon: '⭐' },
    { id: 'other', label: 'Other', icon: '📌' }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
            {isEdit ? 'Edit Employee' : 'Add New Employee'} - 140+ Fields
          </h1>
          <button onClick={() => navigate('/employees')} className="px-4 py-2 rounded-lg border">
            ← Back
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-x-auto">
          <div className="flex border-b">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-b-2 text-[#2b3380]'
                    : 'text-gray-600 hover:text-[#2b3380]'
                }`}
                style={activeTab === tab.id ? { borderColor: '#2b3380', color: '#2b3380' } : {}}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-md p-6">
            
            {/* Basic Information */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Basic Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} required />
                  <InputField label="Middle Name" name="middle_name" value={formData.middle_name} onChange={handleChange} />
                  <InputField label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} required />
                  <InputField label="Date of Birth" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} type="date" />
                  <InputField label="Age" name="age" value={formData.age} onChange={handleChange} type="number" />
                  <SelectField label="Gender" name="gender" value={formData.gender} onChange={handleChange} options={['Male', 'Female', 'Other']} />
                  <InputField label="Blood Group" name="blood_group" value={formData.blood_group} onChange={handleChange} placeholder="e.g., O+, A+, B+" />
                  <SelectField label="Marital Status" name="marital_status" value={formData.marital_status} onChange={handleChange} options={['Single', 'Married', 'Divorced', 'Widowed']} />
                  <InputField label="Profile Photo URL" name="profile_photo_url" value={formData.profile_photo_url} onChange={handleChange} />
                  <InputField label="Personal Email" name="personal_email" value={formData.personal_email} onChange={handleChange} type="email" />
                  <InputField label="Official Email" name="official_email" value={formData.official_email} onChange={handleChange} type="email" required />
                  <InputField label="Mobile Number" name="mobile_number" value={formData.mobile_number} onChange={handleChange} type="tel" required />
                  <InputField label="Alternate Mobile" name="alternate_mobile" value={formData.alternate_mobile} onChange={handleChange} type="tel" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Current Address</label>
                    <textarea name="current_address" value={formData.current_address || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Permanent Address</label>
                    <textarea name="permanent_address" value={formData.permanent_address || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                </div>
              </div>
            )}

            {/* Employment Details */}
            {activeTab === 'employment' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Employment Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Date of Joining" name="date_of_joining" value={formData.date_of_joining} onChange={handleChange} type="date" required />
                  <InputField label="Probation End Date" name="probation_end_date" value={formData.probation_end_date} onChange={handleChange} type="date" />
                  <SelectFieldWithData label="Employment Type" name="employment_type" value={formData.employment_type} onChange={handleChange} data={masterData.employment_types} required />
                  <SelectField label="Employee Status" name="employee_status" value={formData.employee_status} onChange={handleChange} options={['Active', 'Inactive', 'Resigned', 'Terminated', 'On Leave']} />
                  <SelectFieldWithData 
                    label="Department" 
                    name="departments" 
                    value={formData.departments?.[0] || ''} 
                    onChange={(e) => setFormData({...formData, departments: e.target.value ? [e.target.value] : []})} 
                    data={masterData.departments} 
                    labelKey="name"
                    required 
                  />
                  <SelectFieldWithData label="Designation" name="designation" value={formData.designation} onChange={handleChange} data={masterData.designations} labelKey="title" required />
                  <SelectFieldWithData 
                    label="Reporting Manager" 
                    name="reporting_manager_id" 
                    value={formData.reporting_manager_id} 
                    onChange={handleChange} 
                    data={masterData.employees} 
                    valueKey="id"
                    labelKey={(emp) => `${emp.first_name} ${emp.last_name} (${emp.employee_id})`}
                  />
                  <SelectFieldWithData 
                    label="Secondary Manager" 
                    name="secondary_manager_id" 
                    value={formData.secondary_manager_id} 
                    onChange={handleChange} 
                    data={masterData.employees} 
                    valueKey="id"
                    labelKey={(emp) => `${emp.first_name} ${emp.last_name} (${emp.employee_id})`}
                  />
                  <SelectFieldWithData label="Work Location" name="work_location" value={formData.work_location} onChange={handleChange} data={masterData.locations} labelKey="name" />
                  <InputField label="Office Branch" name="office_branch" value={formData.office_branch} onChange={handleChange} />
                  <InputField label="Employee Category" name="employee_category" value={formData.employee_category} onChange={handleChange} />
                  <SelectFieldWithData label="Shift Timing" name="shift_timing" value={formData.shift_timing} onChange={handleChange} data={masterData.shift_timings} labelKey="name" />
                  <SelectField label="Work Mode" name="work_mode" value={formData.work_mode} onChange={handleChange} options={['Office', 'Remote', 'Hybrid']} />
                  <InputField label="Confirmation Date" name="confirmation_date" value={formData.confirmation_date} onChange={handleChange} type="date" />
                  <InputField label="Last Working Date" name="last_working_date" value={formData.last_working_date} onChange={handleChange} type="date" />
                  <InputField label="Notice Period (days)" name="notice_period_days" value={formData.notice_period_days} onChange={handleChange} type="number" />
                  <InputField label="Separation Reason" name="separation_reason" value={formData.separation_reason} onChange={handleChange} />
                  <InputField label="Official ID Card Number" name="official_id_card_number" value={formData.official_id_card_number} onChange={handleChange} />
                </div>
              </div>
            )}

            {/* Compensation & Salary */}
            {activeTab === 'compensation' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Compensation & Salary (₹)</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <InputField label="Basic Salary" name="basic_salary" value={formData.basic_salary} onChange={handleChange} type="number" step="0.01" required />
                  <InputField label="Gross Salary" name="gross_salary" value={formData.gross_salary} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Net Salary" name="net_salary" value={formData.net_salary} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="CTC Annual" name="ctc_annual" value={formData.ctc_annual} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="CTC Monthly" name="ctc_monthly" value={formData.ctc_monthly} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="HRA" name="hra" value={formData.hra} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="DA" name="da" value={formData.da} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Conveyance Allowance" name="conveyance_allowance" value={formData.conveyance_allowance} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Medical Allowance" name="medical_allowance" value={formData.medical_allowance} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Special Allowance" name="special_allowance" value={formData.special_allowance} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Other Allowances" name="other_allowances" value={formData.other_allowances} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="PF Employee" name="pf_employee" value={formData.pf_employee} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="PF Employer" name="pf_employer" value={formData.pf_employer} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="ESI Employee" name="esi_employee" value={formData.esi_employee} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="ESI Employer" name="esi_employer" value={formData.esi_employer} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Professional Tax" name="professional_tax" value={formData.professional_tax} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Income Tax TDS" name="income_tax_tds" value={formData.income_tax_tds} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Minimum Wage Amount" name="minimum_wage_amount" value={formData.minimum_wage_amount} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Salary Adjustments" name="salary_adjustments" value={formData.salary_adjustments} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Arrears" name="arrears" value={formData.arrears} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Bonus" name="bonus" value={formData.bonus} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Incentives" name="incentives" value={formData.incentives} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Commission" name="commission" value={formData.commission} onChange={handleChange} type="number" step="0.01" />
                </div>
                <div className="pt-4">
                  <CheckboxField label="Minimum Wage Applicable" name="minimum_wage_applicable" checked={formData.minimum_wage_applicable} onChange={handleChange} />
                </div>
                <h3 className="text-lg font-semibold mt-6 mb-3">Bank Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Bank Name" name="bank_name" value={formData.bank_name} onChange={handleChange} />
                  <InputField label="Account Number" name="account_number" value={formData.account_number} onChange={handleChange} />
                  <InputField label="IFSC Code" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} />
                  <InputField label="Bank Branch" name="bank_branch" value={formData.bank_branch} onChange={handleChange} />
                  <SelectField label="Payment Mode" name="payment_mode" value={formData.payment_mode} onChange={handleChange} options={['Bank Transfer', 'Cash', 'Cheque']} />
                </div>
              </div>
            )}

            {/* Government IDs & Compliance */}
            {activeTab === 'govt_ids' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Government IDs & Compliance</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="PAN Number" name="pan_number" value={formData.pan_number} onChange={handleChange} />
                  <InputField label="Aadhaar Number" name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} />
                  <InputField label="PF Account Number" name="pf_account_number" value={formData.pf_account_number} onChange={handleChange} />
                  <InputField label="ESI Number" name="esi_number" value={formData.esi_number} onChange={handleChange} />
                  <InputField label="Passport Number" name="passport_number" value={formData.passport_number} onChange={handleChange} />
                  <InputField label="Passport Expiry" name="passport_expiry" value={formData.passport_expiry} onChange={handleChange} type="date" />
                  <InputField label="Driving License Number" name="driving_license_number" value={formData.driving_license_number} onChange={handleChange} />
                  <InputField label="DL Expiry" name="dl_expiry" value={formData.dl_expiry} onChange={handleChange} type="date" />
                  <InputField label="Voter ID" name="voter_id" value={formData.voter_id} onChange={handleChange} />
                  <InputField label="Ration Card Number" name="ration_card_number" value={formData.ration_card_number} onChange={handleChange} />
                  <InputField label="GST Number" name="gst_number" value={formData.gst_number} onChange={handleChange} />
                </div>
              </div>
            )}

            {/* NAPS & NATS Integration */}
            {activeTab === 'naps_nats' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">NAPS & NATS Integration</h2>
                <div className="bg-[#2b338015] border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-[#2b3380]">
                    🎓 Track National Apprenticeship Promotion Scheme (NAPS) and National Apprenticeship Training Scheme (NATS) enrollments
                  </p>
                </div>
                <h3 className="text-lg font-semibold">NAPS Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="NAPS Registration Number" name="naps_registration_number" value={formData.naps_registration_number} onChange={handleChange} />
                  <InputField label="NAPS Status" name="naps_status" value={formData.naps_status} onChange={handleChange} placeholder="e.g., Active, Completed" />
                  <InputField label="NAPS Stipend Amount (₹)" name="naps_stipend_amount" value={formData.naps_stipend_amount} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="NAPS Start Date" name="naps_start_date" value={formData.naps_start_date} onChange={handleChange} type="date" />
                  <InputField label="NAPS End Date" name="naps_end_date" value={formData.naps_end_date} onChange={handleChange} type="date" />
                  <InputField label="NAPS Certificate Number" name="naps_certificate_number" value={formData.naps_certificate_number} onChange={handleChange} />
                  <InputField label="NAPS Trade/Occupation" name="naps_trade_occupation" value={formData.naps_trade_occupation} onChange={handleChange} />
                </div>
                <h3 className="text-lg font-semibold mt-6">NATS Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="NATS Registration Number" name="nats_registration_number" value={formData.nats_registration_number} onChange={handleChange} />
                  <InputField label="NATS Training Period" name="nats_training_period" value={formData.nats_training_period} onChange={handleChange} placeholder="e.g., 6 months, 1 year" />
                  <InputField label="NATS Trade Name" name="nats_trade_name" value={formData.nats_trade_name} onChange={handleChange} />
                  <InputField label="NATS Stipend (₹)" name="nats_stipend" value={formData.nats_stipend} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="NATS Certificate Status" name="nats_certificate_status" value={formData.nats_certificate_status} onChange={handleChange} placeholder="e.g., Issued, Pending" />
                </div>
              </div>
            )}

            {/* Emergency Contacts */}
            {activeTab === 'emergency' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Emergency Contacts</h2>
                <h3 className="text-lg font-semibold">Primary Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Contact Name 1" name="emergency_contact_name_1" value={formData.emergency_contact_name_1} onChange={handleChange} />
                  <InputField label="Relationship 1" name="emergency_contact_relationship_1" value={formData.emergency_contact_relationship_1} onChange={handleChange} />
                  <InputField label="Phone 1" name="emergency_contact_phone_1" value={formData.emergency_contact_phone_1} onChange={handleChange} type="tel" />
                </div>
                <h3 className="text-lg font-semibold mt-6">Secondary Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Contact Name 2" name="emergency_contact_name_2" value={formData.emergency_contact_name_2} onChange={handleChange} />
                  <InputField label="Relationship 2" name="emergency_contact_relationship_2" value={formData.emergency_contact_relationship_2} onChange={handleChange} />
                  <InputField label="Phone 2" name="emergency_contact_phone_2" value={formData.emergency_contact_phone_2} onChange={handleChange} type="tel" />
                </div>
                <h3 className="text-lg font-semibold mt-6">Additional</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Emergency Address</label>
                    <textarea name="emergency_address" value={formData.emergency_address || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <InputField label="Emergency Email" name="emergency_email" value={formData.emergency_email} onChange={handleChange} type="email" />
                </div>
              </div>
            )}

            {/* Education & Qualifications */}
            {activeTab === 'education' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Education & Qualifications</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Highest Qualification" name="highest_qualification" value={formData.highest_qualification} onChange={handleChange} />
                  <InputField label="University/Board" name="university_board" value={formData.university_board} onChange={handleChange} />
                  <InputField label="Year of Passing" name="year_of_passing" value={formData.year_of_passing} onChange={handleChange} type="number" />
                  <InputField label="Specialization/Stream" name="specialization_stream" value={formData.specialization_stream} onChange={handleChange} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Additional Certifications</label>
                    <textarea name="additional_certifications" value={formData.additional_certifications || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Professional Memberships</label>
                    <textarea name="professional_memberships" value={formData.professional_memberships || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Skills & Expertise</label>
                    <textarea name="skills_expertise" value={formData.skills_expertise || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Languages Known</label>
                    <textarea name="languages_known" value={formData.languages_known || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                </div>
              </div>
            )}

            {/* Previous Employment */}
            {activeTab === 'previous' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Previous Employment</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Previous Company Name" name="previous_company_name" value={formData.previous_company_name} onChange={handleChange} />
                  <InputField label="Previous Designation" name="previous_designation" value={formData.previous_designation} onChange={handleChange} />
                  <InputField label="Previous Salary (₹)" name="previous_salary" value={formData.previous_salary} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Employment Duration" name="previous_employment_duration" value={formData.previous_employment_duration} onChange={handleChange} placeholder="e.g., 2 years" />
                  <InputField label="Reason for Leaving" name="reason_for_leaving" value={formData.reason_for_leaving} onChange={handleChange} />
                  <InputField label="Total Years of Experience" name="total_years_of_experience" value={formData.total_years_of_experience} onChange={handleChange} type="number" step="0.1" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                  <CheckboxField label="Experience Letter Available" name="experience_letter_available" checked={formData.experience_letter_available} onChange={handleChange} />
                  <CheckboxField label="Relieving Letter Available" name="relieving_letter_available" checked={formData.relieving_letter_available} onChange={handleChange} />
                </div>
              </div>
            )}

            {/* Documents */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Documents</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Resume URL" name="resume_url" value={formData.resume_url} onChange={handleChange} />
                  <InputField label="Offer Letter URL" name="offer_letter_url" value={formData.offer_letter_url} onChange={handleChange} />
                  <InputField label="Appointment Letter URL" name="appointment_letter_url" value={formData.appointment_letter_url} onChange={handleChange} />
                  <InputField label="Contract Document URL" name="contract_document_url" value={formData.contract_document_url} onChange={handleChange} />
                  <InputField label="Address Proof URL" name="address_proof_url" value={formData.address_proof_url} onChange={handleChange} />
                  <InputField label="Photo ID URL" name="photo_id_url" value={formData.photo_id_url} onChange={handleChange} />
                </div>
              </div>
            )}

            {/* Leave & Attendance */}
            {activeTab === 'attendance' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Leave & Attendance Tracking</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Total Leave Balance" name="total_leave_balance" value={formData.total_leave_balance} onChange={handleChange} type="number" step="0.5" />
                  <InputField label="Sick Leave Balance" name="sick_leave_balance" value={formData.sick_leave_balance} onChange={handleChange} type="number" step="0.5" />
                  <InputField label="Casual Leave Balance" name="casual_leave_balance" value={formData.casual_leave_balance} onChange={handleChange} type="number" step="0.5" />
                  <InputField label="Leaves Taken (Current Year)" name="leaves_taken_current_year" value={formData.leaves_taken_current_year} onChange={handleChange} type="number" step="0.5" />
                  <InputField label="Total Present Days" name="total_present_days" value={formData.total_present_days} onChange={handleChange} type="number" />
                  <InputField label="Total Absent Days" name="total_absent_days" value={formData.total_absent_days} onChange={handleChange} type="number" />
                  <InputField label="Late Coming Count" name="late_coming_count" value={formData.late_coming_count} onChange={handleChange} type="number" />
                  <InputField label="Half Days Count" name="half_days_count" value={formData.half_days_count} onChange={handleChange} type="number" />
                  <InputField label="Last Attendance Date" name="last_attendance_date" value={formData.last_attendance_date} onChange={handleChange} type="date" />
                </div>
              </div>
            )}

            {/* Performance & Reviews */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Performance & Reviews</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Last Appraisal Date" name="last_appraisal_date" value={formData.last_appraisal_date} onChange={handleChange} type="date" />
                  <InputField label="Next Appraisal Due Date" name="next_appraisal_due_date" value={formData.next_appraisal_due_date} onChange={handleChange} type="date" />
                  <InputField label="Current Performance Rating" name="current_performance_rating" value={formData.current_performance_rating} onChange={handleChange} />
                  <InputField label="Last Increment %" name="last_increment_percentage" value={formData.last_increment_percentage} onChange={handleChange} type="number" step="0.01" />
                  <InputField label="Last Increment Date" name="last_increment_date" value={formData.last_increment_date} onChange={handleChange} type="date" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Promotion History</label>
                  <textarea name="promotion_history" value={formData.promotion_history || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="3" />
                </div>
              </div>
            )}

            {/* Other/Notes */}
            {activeTab === 'other' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Notes & Comments</h2>
                <div>
                  <label className="block text-sm font-medium mb-1">General Notes</label>
                  <textarea name="notes" value={formData.notes || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="4" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Internal Comments (Not visible to employee)</label>
                  <textarea name="internal_comments" value={formData.internal_comments || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="4" />
                </div>
              </div>
            )}

          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={() => navigate('/employees')}
              className="px-6 py-3 rounded-lg font-medium border hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-lg text-white font-medium"
              style={{backgroundColor: loading ? '#6b7280' : '#2b3380'}}
            >
              {loading ? 'Saving...' : isEdit ? 'Update Employee' : 'Create Employee'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default EmployeeFormComprehensive;
